import { Card, CardContent, CardHeader, CardTitle, CardDescription, Button } from '@powercore/ui';
import { LogIn, LogOut, User, Shield, Key, CheckCircle, XCircle, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../platform';

export default function AuthPage() {
  const { isAuthenticated, isLoading, user, accessToken, signIn, signOut } = useAuth();
  const [apiResponse, setApiResponse] = useState<unknown>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [testingApi, setTestingApi] = useState(false);

  const testAuthenticatedApi = async () => {
    setTestingApi(true);
    setApiError(null);
    setApiResponse(null);

    try {
      const result = await api.me();
      if (result.error) {
        setApiError(result.error.message);
      } else {
        setApiResponse(result.data);
      }
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setTestingApi(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mazo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Authentication</h1>
        <p className="text-gray-500">
          Logto authentication integration demo
        </p>
      </div>

      {/* Auth Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Authentication Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isAuthenticated ? (
                <>
                  <div className="rounded-full bg-green-100 p-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <div className="font-medium text-green-700">Authenticated</div>
                    <div className="text-sm text-gray-500">
                      You are signed in with Logto
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="rounded-full bg-gray-100 p-2">
                    <XCircle className="h-5 w-5 text-gray-400" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-700">Not Authenticated</div>
                    <div className="text-sm text-gray-500">
                      Sign in to access protected features
                    </div>
                  </div>
                </>
              )}
            </div>
            {isAuthenticated ? (
              <Button variant="outline" onClick={() => signOut()}>
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </Button>
            ) : (
              <Button onClick={() => signIn()}>
                <LogIn className="mr-2 h-4 w-4" />
                Sign In
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* User Info */}
      {isAuthenticated && user && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              User Information
            </CardTitle>
            <CardDescription>
              Claims from your ID token
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-mazo-100">
                <User className="h-8 w-8 text-mazo-600" />
              </div>
              <div className="flex-1 space-y-2">
                <div>
                  <div className="text-lg font-medium">
                    {user.name || 'No name provided'}
                  </div>
                  <div className="flex items-center gap-2 text-gray-500">
                    {user.email}
                  </div>
                </div>
                <div className="text-xs text-gray-400">
                  User ID: {user.id}
                </div>
              </div>
            </div>

            <div className="mt-4">
              <h4 className="mb-2 font-medium">User Object</h4>
              <pre className="overflow-auto rounded-lg bg-gray-50 p-3 text-xs">
                {JSON.stringify(user, null, 2)}
              </pre>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Access Token */}
      {isAuthenticated && accessToken && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              ID Token
            </CardTitle>
            <CardDescription>
              JWT token for API authentication
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h4 className="mb-2 font-medium">Token Preview</h4>
                <div className="overflow-auto rounded-lg bg-gray-50 p-3">
                  <code className="break-all text-xs">
                    {accessToken.substring(0, 100)}...
                  </code>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={testAuthenticatedApi} loading={testingApi}>
                  Test /api/me Endpoint
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigator.clipboard.writeText(accessToken)}
                >
                  Copy Full Token
                </Button>
              </div>
              {apiError && (
                <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
                  <strong>API Error:</strong> {apiError}
                </div>
              )}
              {apiResponse != null && (
                <div>
                  <h4 className="mb-2 font-medium">API Response</h4>
                  <pre className="overflow-auto rounded-lg bg-green-50 p-3 text-xs">
                    {JSON.stringify(apiResponse, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Logto Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Logto Configuration</CardTitle>
          <CardDescription>
            Current authentication settings
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Logto Endpoint</span>
              <code className="text-gray-900">
                {import.meta.env.VITE_LOGTO_ENDPOINT || 'Not configured'}
              </code>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">App ID</span>
              <code className="text-gray-900">
                {import.meta.env.VITE_LOGTO_APP_ID || 'Not configured'}
              </code>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Callback URL</span>
              <code className="text-gray-900">{`${window.location.origin}/callback`}</code>
            </div>
          </div>

          <div className="mt-4 rounded-lg bg-mazo-50 p-4">
            <h4 className="font-medium text-mazo-900">Integration Notes</h4>
            <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-mazo-800">
              <li>Configure Logto via environment variables (VITE_ prefix)</li>
              <li>Add callback URL to Logto application settings</li>
              <li>Using ID tokens for API authentication (verifiable JWT)</li>
              <li>Backend verifies JWT using Logto JWKS endpoint</li>
            </ul>
            <a
              href="https://docs.logto.io/"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1 text-sm text-mazo-600 hover:underline"
            >
              Logto Documentation
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
